import { Grid, Box } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useState } from "react";
import {
  GenericConnectInput,
  UpdateEmployeeInput,
  useGetEmployeesStationNameManyQuery,
  useGetEmployeeInfoQuery,
  useGetEmployeeRoleNamesPossibleQuery,
  useGetEmployeeRoleNamesQuery,
  useGetLocationsQuery,
  useUpdateEmployeeMutation,
} from "src/generated/graphql";
import { useParams } from "react-router-dom";
import TextInput from "src/components/TextInput/TextInput";
import { useDisclosure } from "@mantine/hooks";
import MultiSelect from "src/components/MultiSelect";
import ImageUpload from "src/components/ImageUpload";
import { EMPLOYEE_ROLE_OBJ, MESSAGES } from "src/utils/constants";
import FormCard from "src/components/FormCard";
import { useAppContext } from "src/api/AppContext";
import { array, object, string } from "yup";
import Select, { ISelect } from "src/components/Select";
import useWritePermission from "src/hooks/usePermission";

type IFormProps = Omit<
  UpdateEmployeeInput,
  "locations" | "stations" | "role"
> & {
  locations: string[] | undefined;
  stations: string[] | undefined;
  role?: string;
};

const EmployeeDetail = () => {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.locEmployees);
  const isAllowWriteStation = useWritePermission(
    EMPLOYEE_ROLE_OBJ.employeeStations
  );
  const { empId } = useParams<{ empId: string }>();
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(true);
  const { currentEmployee, currentLocation, currentAdmin } = useAppContext();
  const [rolesArr, setrolesArr] = useState<ISelect[]>([]);
  const { data: locArr } = useGetLocationsQuery();
  const [otherLocStataions, setotherLocStataions] = useState<
    GenericConnectInput[]
  >([]);

  useGetEmployeeRoleNamesQuery({
    skip: !currentAdmin,
    onCompleted(data) {
      if (!data.employeeRoleMany) return;
      setrolesArr(
        data.employeeRoleMany.map((e) => ({
          value: e.id,
          label: e.name,
        }))
      );
    },
  });

  const { data: empNames } = useGetEmployeeRoleNamesPossibleQuery({
    skip: !currentEmployee,
    onCompleted() {
      createNamesArr();
      mergeCurrentRole();
    },
  });

  const createNamesArr = () => {
    if (!empNames?.employeeRolesPossible) return;
    setrolesArr(
      empNames.employeeRolesPossible.map((e) => ({
        value: e.id,
        label: e.name,
      }))
    );
  };

  const mergeCurrentRole = () => {
    if (currentEmployee && empData?.employeeOne?.role) {
      const { role } = empData.employeeOne;
      const roleExists = rolesArr.some(
        (existingRole) => existingRole.value === role.id
      );
      if (!roleExists) {
        setrolesArr([
          ...rolesArr,
          {
            value: role.id!,
            label: role.name!,
          },
        ]);
      }
    }
  };

  const { data: empData, refetch: refetchEmp } = useGetEmployeeInfoQuery({
    variables: { id: empId },
    onCompleted: () => {
      resetFormState();
      mergeCurrentRole();
    },
    skip: !empId || !locArr,
  });

  const [updateEmployee, { loading }] = useUpdateEmployeeMutation({
    onCompleted: () => {
      toggleFormActive();
      form.resetDirty();
      refetchEmp();
      createNamesArr();
    },
  });

  const employeeFormSchema = object().shape({
    staffId: string()
      .nullable()
      .matches(/^\S*$/, "must not contain spaces")
      .max(20, "Staff ID must not be greater than 20 letters"),
    firstName: string()
      .trim("No spaces allowed")
      .strict(true)
      .min(2, "First Name should must be least 2 letters")
      .max(30, "First Name must not be greater than 30 letters")
      .required("First Name is required"),
    middleName: string()
      .nullable()
      .trim("No spaces allowed")
      .strict(true)
      .matches(/.{2,}/, {
        excludeEmptyString: true,
        message: "Middle Name must be at least 2 characters",
      })
      .max(30, "Middle Name must not be greater than 30 letters"),
    lastName: string()
      .trim("No spaces allowed")
      .strict(true)
      .min(2, "Last Name should must be least 2 letters")
      .max(30, "Last Name must not be greater than 30 letters")
      .required("Last Name is required"),
    email: string().email().required("Email is required").max(50),
    phoneNumber: string()
      .matches(/^(?:\+\d{9,11}|\d{10,11})?$/, {
        excludeEmptyString: true,
        message: "Enter a valid phone number",
      })
      .notRequired(),
    locations: array()
      .min(1, "at least 1 location is required")
      .required("Location is required"),
    role: string().required("Role is required"),
  });

  const empInitialValues = {
    staffId: "",
    firstName: "",
    middleName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    profilePicUrl: "",
    locations: [],
    stations: [],
    role: "",
  };

  const form = useForm<IFormProps>({
    initialValues: empInitialValues,
    validate: yupResolver(employeeFormSchema),
  });

  const { data: stations } = useGetEmployeesStationNameManyQuery({
    variables: { locationId: currentLocation?.id || "" },
    skip: !currentLocation || !empData?.employeeOne,
    onCompleted(data) {
      if (!data.employeeStationMany) return;

      // Only show matching Stations with Current location / Store otherLocStataions
      let matchingStations: string[] = [],
        _otherLocStations: GenericConnectInput[] = [];
      if (data?.employeeStationMany) {
        empData?.employeeOne!.stations?.forEach((station) => {
          const isMatching = data.employeeStationMany!.some(
            (employeeStation) => employeeStation.id === station.id
          );

          isMatching
            ? matchingStations.push(station.id)
            : _otherLocStations.push({ id: station.id });
        });
      }
      form.setFieldValue("stations", matchingStations);
      setotherLocStataions(_otherLocStations);
    },
    fetchPolicy: "network-only",
  });

  const handleUpdate = async (values: IFormProps) => {
    if (!empId) return;
    const { pin, ...rest } = values;

    let mergedStations: GenericConnectInput[] = [];
    if (values.stations?.length) {
      mergedStations = values.stations.map((e: string) => ({ id: e }));
    }
    if (otherLocStataions.length) {
      mergedStations = [...mergedStations, ...otherLocStataions];
    }
    updateEmployee({
      variables: {
        data: {
          ...rest,
          locations: values.locations?.map((e: string) => ({ id: e })),
          stations: mergedStations,
          ...(values.pin && { pin: values.pin }),
          role: { id: values.role! },
        },
        id: empId,
      },
    });
  };

  const resetFormState = () => {
    const { user, locations, staffId, pin, stations, role } =
      empData?.employeeOne || {};
    const {
      firstName,
      middleName,
      lastName,
      email,
      phoneNumber,
      profilePicUrl,
    } = user || {};
    let _locations = locations?.map((val) => val.id);
    let _data = {
      staffId,
      firstName,
      middleName,
      lastName,
      email,
      phoneNumber,
      locations: _locations,
      stations: stations?.map((val) => val.id),
      profilePicUrl,
      pin: pin,
      role: role?.id,
    };
    form.setValues(_data);
    form.resetDirty(_data);
  };

  const handleEdit = () => {
    toggleFormActive();
  };

  const handleCancel = () => {
    resetFormState();
    toggleFormActive();
  };

  const handlePinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (/^\d*$/.test(event.target.value)) {
      form.setFieldValue("pin", event.target.value);
    }
  };

  const isFormUpdated = !isFormActive && form.isDirty() && form.isTouched();
  return (
    <>
      <FormCard
        title={"Basic Info"}
        loading={loading}
        bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
        onCancel={handleCancel}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) => handleUpdate(values))}
        isFormActive={isFormActive}
      >
        <Box mx={18} mb="sm">
          <Grid>
            <Grid.Col span={12} md={5} mt={"sm"}>
              <ImageUpload
                disabled={isFormActive}
                onReadSuccess={(b64) =>
                  form.setFieldValue("profilePicUrl", b64)
                }
                src={form.values.profilePicUrl || ""}
                alt="Account Image"
                sx={{
                  maxHeight: form.getInputProps("profilePicUrl").value
                    ? "auto"
                    : 300,
                  maxWidth: "auto",
                }}
              />
            </Grid.Col>
            <Grid.Col xs={12} md={7}>
              <TextInput
                label="Staff ID"
                {...form.getInputProps("staffId")}
                disabled={isFormActive}
              />
              <TextInput
                withAsterisk
                label="First Name"
                {...form.getInputProps("firstName")}
                disabled={isFormActive}
              />
              <TextInput
                label="Middle Name"
                {...form.getInputProps("middleName")}
                disabled={isFormActive}
              />
              <TextInput
                withAsterisk
                label="Last Name"
                {...form.getInputProps("lastName")}
                disabled={isFormActive}
              />
              <TextInput
                withAsterisk
                label="Email"
                {...form.getInputProps("email")}
                disabled={true}
              />
              <TextInput
                label="Phone Number"
                {...form.getInputProps("phoneNumber")}
                disabled={isFormActive}
              />

              {currentEmployee?.id === empId && (
                <TextInput
                  label="Pin"
                  disabled={isFormActive}
                  maxLength={4}
                  {...form.getInputProps("pin")}
                  onChange={handlePinChange}
                />
              )}

              <Select
                data={rolesArr}
                label="Select role"
                disabled={isFormActive}
                {...form.getInputProps("role")}
              />

              <MultiSelect
                data={
                  locArr?.locationMany?.map((val) => ({
                    value: val.id,
                    label: val.name,
                  })) || []
                }
                disabled={isFormActive}
                label="Select Locations"
                {...form.getInputProps("locations")}
              />
              {isAllowWriteStation && !!currentLocation?.stationsEnabled && (
                <MultiSelect
                  data={
                    stations?.employeeStationMany?.map((val) => ({
                      value: val.id,
                      label: val.name,
                    })) || []
                  }
                  disabled={isFormActive}
                  label="Select Stations"
                  {...form.getInputProps("stations")}
                />
              )}
            </Grid.Col>
          </Grid>
        </Box>
      </FormCard>
    </>
  );
};

export default EmployeeDetail;
